/**
 * AstraDraw Logo Component
 *
 * Renders the AstraDraw brand logo with icon and optional text.
 *
 * DESIGN SPECIFICATIONS (from Figma):
 * - Icon: Triangle with sparkle/star shape, purple→blue gradient
 * - Text: "AstraDraw" using Alexandria Google Font
 *   - "Astra" = Bold (700 weight)
 *   - "Draw" = Light (300 weight)
 *   - Letter spacing: 5% (0.05em)
 * - Brand colors: #674CDC (purple) → #1E8CE5 (blue) gradient
 *
 * USAGE:
 * <AstradrawLogo size="large" withText />  // Full logo with text
 * <AstradrawLogo size="small" />           // Icon only
 * <AstradrawIcon />                        // Just the icon component
 *
 * SIZES:
 * - xs, small, normal, large, custom, mobile
 * - See AstradrawLogo.module.scss for size values
 */
import styles from "./AstradrawLogo.module.scss";

/**
 * Logo Icon - Triangle with sparkle shape
 * Uses linear gradient from purple (#674CDC) to blue (#1E8CE5)
 */
const LogoIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 54 49"
    className={styles.icon}
    fill="none"
  >
    <path
      d="M19.7029 4C22.7821 -1.33333 30.4801 -1.33333 33.5593 4L52.1794 36.25C55.2583 41.5833 51.409 48.25 45.2507 48.25H8.01147C1.85322 48.2498 -1.99537 41.5833 1.08374 36.25L19.7029 4ZM26.1355 14.6494C26.0664 14.6494 25.9987 14.6717 25.9431 14.7129C25.8876 14.7541 25.847 14.8128 25.8269 14.8789L24.658 18.7998C24.1834 20.3914 23.3197 21.8403 22.1453 23.0146C20.971 24.1888 19.5228 25.0519 17.9314 25.5264L14.0105 26.6963C13.9445 26.7164 13.8865 26.7571 13.8455 26.8125C13.8044 26.868 13.782 26.9358 13.782 27.0049C13.7821 27.0738 13.8044 27.1409 13.8455 27.1963C13.8866 27.2517 13.9445 27.2924 14.0105 27.3125L17.9314 28.4824C19.5229 28.9569 20.9709 29.8209 22.1453 30.9951C23.3196 32.1694 24.1834 33.6175 24.658 35.209L25.8269 39.1309C25.8471 39.1969 25.8886 39.2548 25.9441 39.2959C25.9995 39.3368 26.0666 39.3593 26.1355 39.3594C26.2046 39.3594 26.2724 39.337 26.3279 39.2959C26.3833 39.2548 26.4239 39.1968 26.4441 39.1309L27.614 35.209C28.0885 33.6176 28.9516 32.1694 30.1257 30.9951C31.3 29.8209 32.7482 28.957 34.3396 28.4824L38.2615 27.3135C38.3275 27.2933 38.3854 27.2518 38.4265 27.1963C38.4675 27.1408 38.49 27.0738 38.49 27.0049C38.49 26.9358 38.4676 26.868 38.4265 26.8125C38.3854 26.7571 38.3274 26.7164 38.2615 26.6963L34.3396 25.5273C32.7482 25.0528 31.3 24.1889 30.1257 23.0146C28.9514 21.8403 28.0875 20.3914 27.613 18.7998L26.4441 14.8789C26.424 14.8127 26.3834 14.7541 26.3279 14.7129C26.2723 14.6717 26.2046 14.6494 26.1355 14.6494Z"
      fill="url(#astradraw-gradient)"
    />
    <defs>
      <linearGradient
        id="astradraw-gradient"
        x1="11.2661"
        y1="59.5556"
        x2="58.4409"
        y2="1.34553"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.0398321" stopColor="#674CDC" />
        <stop offset="1" stopColor="#1E8CE5" />
      </linearGradient>
    </defs>
  </svg>
);

/**
 * Logo Text - "AstraDraw" in Alexandria font
 * - "Astra" in bold (700)
 * - "Draw" in light (300)
 * - Centered using textAnchor="middle"
 */
const LogoText = () => (
  <svg
    viewBox="0 0 220 40"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    className={styles.text}
  >
    <text
      x="110"
      y="30"
      fill="currentColor"
      fontFamily="'Alexandria', sans-serif"
      fontSize="32"
      letterSpacing="0.05em"
      textAnchor="middle"
    >
      <tspan fontWeight="700">Astra</tspan>
      <tspan fontWeight="300">Draw</tspan>
    </text>
  </svg>
);

type LogoSize = "xs" | "small" | "normal" | "large" | "custom" | "mobile";

export interface AstradrawLogoProps {
  size?: LogoSize;
  withText?: boolean;
  style?: React.CSSProperties;
}

const sizeClasses: Record<LogoSize, string> = {
  mobile: styles.mobile,
  xs: styles.xs,
  small: styles.small,
  normal: styles.normal,
  large: styles.large,
  custom: "",
};

export const AstradrawLogo = ({
  style,
  size = "small",
  withText,
}: AstradrawLogoProps) => {
  return (
    <div className={`${styles.logo} ${sizeClasses[size]}`} style={style}>
      <LogoIcon />
      {withText && <LogoText />}
    </div>
  );
};

// Also export the icon for use in other places (like menu items)
export const AstradrawIcon = LogoIcon;
